import { forwardRef, memo, type Ref } from "react";
import type { StyleProp, ViewStyle } from "react-native";
import { VideoView, type VideoPlayer as ExpoVideoPlayer } from "expo-video";

/**
 * expo-video merges native (PureComponent) and web (forwardRef) VideoView
 * declarations, collapsing the ref type to `{}`. We re-declare the instance
 * methods that exist on both platforms so consumers can call them safely.
 */
export interface VideoViewRef {
  enterFullscreen(): Promise<void>;
  exitFullscreen(): Promise<void>;
  startPictureInPicture(): Promise<void>;
  stopPictureInPicture(): Promise<void>;
}

interface VideoPlayerViewProps {
  player: ExpoVideoPlayer;
  /**
   * Sizing style handed down by `VideoPlayer`'s `renderVideoSurface` slot —
   * fills the playback box. See `VideoSurfaceRenderProps` in `@visioni/a3s-ui`.
   */
  style: StyleProp<ViewStyle>;
  /** Native controls chrome — shown in fullscreen and landscape, where expo-video's own chrome takes over. */
  nativeControls: boolean;
  onFullscreenEnter: () => void;
  onFullscreenExit: () => void;
  onFirstFrameRender: () => void;
}

const VideoPlayerViewInner = forwardRef<VideoViewRef, VideoPlayerViewProps>(
  ({ player, style, nativeControls, onFullscreenEnter, onFullscreenExit, onFirstFrameRender }, ref) => {
    return (
      <VideoView
        ref={ref as Ref<never>}
        // @ts-expect-error expo-video merges native + web VideoView declarations, creating an unsatisfiable intersection type for `player`
        player={player}
        // Plain inline style (NOT a Unistyles StyleSheet ref), merged with the
        // literal style VideoPlayer hands us. On web, expo-video builds the
        // <video> style as `{ ...StyleSheet.flatten(props.style), objectFit }`.
        // RN's flatten doesn't resolve a Unistyles className-ref into concrete
        // width/height, so the <video> was left with NO dimensions and fell
        // back to its intrinsic size (e.g. 1024×768), which overflow:hidden
        // then cropped. Both `style` and `VIDEO_VIEW_BACKGROUND` are plain
        // literal objects, so the merged array flattens correctly and the
        // video fills the box; `contentFit=contain` letterboxes any aspect
        // ratio mismatch.
        style={[style, VIDEO_VIEW_BACKGROUND]}
        contentFit="contain"
        nativeControls={nativeControls}
        allowsFullscreen={true}
        allowsVideoFrameAnalysis={false}
        onFullscreenEnter={onFullscreenEnter}
        onFullscreenExit={onFullscreenExit}
        onFirstFrameRender={onFirstFrameRender}
      />
    );
  },
);

const VIDEO_VIEW_BACKGROUND = { backgroundColor: "#000" } as const;

export const VideoPlayerView = memo(VideoPlayerViewInner);
